import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import { ArrowForwardIos } from '@mui/icons-material';

import { Application } from '@libs/models';
import { useGetJobseekerApplicationsQuery } from '@carcareers/store';
import { routes } from '@carcareers/utils';

export const JobseekerApplications: FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { data, isLoading, error } = useGetJobseekerApplicationsQuery({
    page,
    limit,
  });

  const applications = data?.items || [];
  const totalItems = data?.meta?.totalItems || 0;
  const totalPages = Math.ceil(totalItems / limit);

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (event: any) => {
    setLimit(event.target.value);
    setPage(1);
  };

  const handleViewJob = (application: Application) => {
    if (application.post?.id) {
      navigate(`/${routes.JOBSEEKERS}/${application.post.id}`);
    }
  };

  const formatAppliedDate = (application: Application) => {
    return application.appliedAt || application.createdAt
      ? new Date(application.appliedAt || application.createdAt).toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        })
      : 'N/A';
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 15 }}>
        <Alert severity="error">Error loading applications. Please try again.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 15 }}>
      <Typography variant="h2" sx={{ mb: 3 }}>
        My Applications
      </Typography>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 4,
          border: `1px solid`,
          borderColor: 'divider',
        }}
      >
        {applications.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h4" color="text.secondary" sx={{ mb: 2 }}>
              No applications found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start applying to jobs to see your applications here.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {applications.map((application, index) => {
              const job = application.post?.job;
              const rooftop = job?.rooftop;

              return (
                <Box key={application.id}>
                  <ListItem
                    sx={{
                      px: 3,
                      py: 2,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                            '&:hover': { cursor: 'pointer' },
                          }}
                          onClick={() => handleViewJob(application)}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h4" sx={{ mb: 0.5 }}>
                              {job?.title || 'Unknown Position'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {rooftop?.name || 'Unknown Company'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Applied: {formatAppliedDate(application)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', color: 'primary.main' }}>
                            <ArrowForwardIos />
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < applications.length - 1 && <Divider />}
                </Box>
              );
            })}
          </List>
        )}
      </Paper>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Rows per page</InputLabel>
          <Select value={limit} label="Rows per page" onChange={handleLimitChange}>
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {(page - 1) * limit + 1}-{Math.min(page * limit, totalItems)} of {totalItems}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      </Box>
    </Container>
  );
};
